/**
 * MONSTER TOWN — WHAT MAY BE MERGED. OWNED BY PERF (src/perf/**).
 *
 * The batcher's policy layer, kept apart from its machinery because this is the
 * part that can break the GAME rather than the frame rate. Merging the wrong
 * mesh into a shared buffer does not crash anything — it quietly stops a prop
 * reacting, which is a LAW #1 violation a kid finds before any test does.
 *
 * See `batcher.ts` for how the merge itself works.
 */

import type * as THREE from 'three'
import { Grid, type Cell, type Placement } from '../core/grid'
import { getPlaceable } from '../core/placeable'
import type { InstanceId, PlaceableId, PlacedInstance, SceneId } from '../core/types'

/**
 * WHICH NAMES MUST SURVIVE THE MERGE — and it is a much shorter list than it
 * looks.
 *
 * First cut: "has a name" means "might animate", because `anim.ts` finds parts
 * with `getObjectByName`. Measured, that vetoed 60% of the geometry and half the
 * instances outright — art builders name nearly every box for their own
 * readability (`brick`, `slab`, `crown`, `seat`, `heads`) and almost none of
 * those are interaction targets.
 *
 * Second cut: ask the def. `interact` is a pure function returning its feedback,
 * and `anim`/`toggle` feedback carries the `target`; running it in a sandbox the
 * way `auditPlaceables` already does harvests exactly the names the runtime will
 * ever look up.
 *
 * Third cut, and the one that actually pays: ANIM TARGETS DO NOT NEED
 * PROTECTING AT ALL. The instant a prop animates, the batcher suspends it — its
 * own meshes come back and the merged copy is hidden — so `anim.ts` finds the
 * real named part and moves it exactly as if batching did not exist. The clip
 * ends where it started, so rejoining the batch afterwards is seamless.
 *
 * What genuinely cannot survive a merge is a PERSISTENT visibility change:
 * `applyToggle` flips `visible` on `x` and `xOff` and leaves it flipped. Those
 * names, and anything under them, are the only geometry that must stay live —
 * and the instance is dropped from batching for good the first time one fires.
 *
 * If the handler throws on a synthetic context, every name is protected: a def
 * we cannot interrogate does not get optimised.
 */
const protectedCache = new Map<string, ReadonlySet<string> | null>()

export function harvestTargets(defId: PlaceableId): ReadonlySet<string> | null {
  const cached = protectedCache.get(defId as string)
  if (cached !== undefined) return cached

  const def = getPlaceable(defId)
  if (def === undefined) {
    protectedCache.set(defId as string, null)
    return null
  }
  const names = new Set<string>()
  let seed = 1337
  const rng = (): number => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0
    return seed / 0x100000000
  }
  const anchor: Cell = { gx: 0, gy: 0, gz: 0 }
  try {
    const state = def.initialState?.() ?? {}
    for (let taps = 0; taps < 6; taps++) {
      const instance: PlacedInstance = {
        id: '__bake' as InstanceId,
        defId,
        sceneId: '__bake' as SceneId,
        anchor,
        rot: 0,
        state,
      }
      const placement: Placement = {
        instanceId: instance.id,
        defId,
        anchor,
        rot: 0,
        footprint: def.footprint,
        connectGroup: def.connect?.group ?? null,
        stackable: def.stackable,
        walkable: def.walkable,
      }
      const result = def.interact({
        def,
        instance,
        placement,
        sceneId: instance.sceneId,
        grid: sandboxGrid(),
        state,
        art: null,
        at: { x: 0, y: 0, z: 0 },
        taps,
        now: 1000 * taps,
        rng,
      })
      for (const f of result.feedback) {
        // `anim` deliberately absent — see the note above.
        if (f.kind !== 'toggle') continue
        const t = f.target ?? 'on'
        names.add(t)
        names.add(`${t}Off`)
      }
    }
  } catch {
    // Cannot interrogate this def — protect everything it named.
    protectedCache.set(defId as string, null)
    return null
  }
  const frozen: ReadonlySet<string> = names
  protectedCache.set(defId as string, frozen)
  return frozen
}

let _sandboxGrid: Grid | null = null
function sandboxGrid(): Grid {
  _sandboxGrid ??= new Grid({ id: '__bake', scale: 'interior', width: 4, depth: 4, levels: 2 })
  return _sandboxGrid
}

/** Kit-generated helper names all carry a colon (`mesh:ink`, `lid:inked`). */
function isKitName(name: string): boolean {
  return name.includes(':')
}

function isProtectedName(name: string, live: ReadonlySet<string> | null): boolean {
  if (name.length === 0) return false
  if (isKitName(name)) return false
  // `null` means the def could not be interrogated: treat every name as live.
  return live === null || live.has(name)
}

/** Why a mesh was left out — diagnostics only, never used by the bake itself. */
export function bakeVeto(
  mesh: THREE.Mesh,
  root: THREE.Object3D,
  live: ReadonlySet<string> | null,
): string | null {
  if (mesh.isMesh !== true) return null
  const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material
  if (mat === undefined || mat === null) return 'noMaterial'
  if (mat.transparent === true) return 'transparent'
  if (mesh.geometry.getIndex() === null) return 'nonIndexed'
  if (isProtectedName(mesh.name, live)) return `live:${mesh.name}`
  let cur: THREE.Object3D | null = mesh.parent
  while (cur !== null && cur !== root) {
    if (isProtectedName(cur.name, live)) return `underLive:${cur.name}`
    cur = cur.parent
  }
  return null
}

/** The protected-name set for a def, for tools that want to explain a bake. */
export function liveNamesFor(defId: PlaceableId): ReadonlySet<string> | null {
  return harvestTargets(defId)
}

export function bakeableMesh(
  mesh: THREE.Mesh,
  root: THREE.Object3D,
  live: ReadonlySet<string> | null,
): boolean {
  if (mesh.isMesh !== true) return false
  if ((mesh as THREE.InstancedMesh).isInstancedMesh === true) return false
  if ((mesh as THREE.BatchedMesh).isBatchedMesh === true) return false
  if (mesh.visible === false) return false
  const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material
  if (mat === undefined || mat === null) return false
  if (mat.transparent === true) return false
  const geo = mesh.geometry
  if (geo === undefined || geo.getIndex() === null) return false
  if (geo.getAttribute('position') === undefined) return false
  if (isProtectedName(mesh.name, live)) return false
  // Anything under a live ancestor moves with it.
  let cur: THREE.Object3D | null = mesh.parent
  while (cur !== null && cur !== root) {
    if (isProtectedName(cur.name, live)) return false
    cur = cur.parent
  }
  return true
}
